import React, {
    useEffect,
    useState
} from 'react';
import { Link } from 'react-router-dom';
import getImageUrl from '../../utils/getImageUrl';
import './portfolioList.css';
import API_BASE_URL from '../../config/api';

const ITEMS_PER_PAGE = 10;
const PAGES_PER_GROUP = 5;

const PortfolioList = () => {
    const categories = [
        '전체',
        'LED 전광판',
        '빔프로젝터',
        '설치',
        '기업행사',
        '전시 · 박람회',
        '종교시설'
    ];

    const [portfolioData, setPortfolioData] =
        useState([]);

    const [isLoading, setIsLoading] =
        useState(true);

    const [loadError, setLoadError] =
        useState('');

    const [category, setCategory] =
        useState('전체');

    const [search, setSearch] =
        useState('');

    const [currentPage, setCurrentPage] =
        useState(1);

    /*
     * =====================================================
     * PORTFOLIO DATA
     * =====================================================
     */

    useEffect(() => {
        const fetchPortfolios = async () => {
            try {
                setIsLoading(true);
                setLoadError('');

                const response = await fetch(
                    `${API_BASE_URL}/api/portfolios/public`
                );

                const result =
                    await response.json();

                if (!response.ok) {
                    throw new Error(
                        result.message ||
                        '시공사례를 불러오지 못했습니다.'
                    );
                }

                const portfolios =
                    (result.portfolios || []).map(
                        (item) => ({
                            id: item.portfolio_id,
                            title: item.title || '',
                            category: item.category || '',
                            location: item.location || '',
                            summary: item.description || '',
                            thumbnail: item.thumbnail_image
                                ? getImageUrl(
                                    item.thumbnail_image
                                )
                                : '',
                            date: item.work_date
                                ? item.work_date.substring(
                                    0,
                                    10
                                )
                                : ''
                        })
                    );

                setPortfolioData(
                    portfolios
                );
            } catch (error) {
                console.error(
                    '시공사례 조회 오류:',
                    error
                );

                setLoadError(
                    error.message
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchPortfolios();
    }, []);

    /*
     * =====================================================
     * FILTER
     * =====================================================
     */

    const filteredPortfolio =
        portfolioData.filter((item) => {
            const categoryMatch =
                category === '전체' ||
                item.category === category;

            const keyword =
                search
                    .trim()
                    .toLowerCase();

            const searchMatch =
                keyword === '' ||
                item.title
                    .toLowerCase()
                    .includes(keyword) ||
                item.summary
                    .toLowerCase()
                    .includes(keyword) ||
                item.location
                    .toLowerCase()
                    .includes(keyword);

            return (
                categoryMatch &&
                searchMatch
            );
        });

    /*
     * =====================================================
     * PAGINATION
     * =====================================================
     */

    const totalPages =
        Math.ceil(
            filteredPortfolio.length /
            ITEMS_PER_PAGE
        );

    const startIndex =
        (currentPage - 1) *
        ITEMS_PER_PAGE;

    const endIndex =
        startIndex +
        ITEMS_PER_PAGE;

    const currentPortfolio =
        filteredPortfolio.slice(
            startIndex,
            endIndex
        );

    /*
     * =====================================================
     * PAGE NUMBER GROUP
     *
     * 1 ~ 5
     * 6 ~ 10
     * 11 ~ 15 ...
     * =====================================================
     */

    const currentGroup =
        Math.floor(
            (currentPage - 1) /
            PAGES_PER_GROUP
        );

    const startPage =
        currentGroup *
        PAGES_PER_GROUP +
        1;

    const endPage =
        Math.min(
            startPage +
            PAGES_PER_GROUP -
            1,
            totalPages
        );

    const visiblePages =
        Array.from(
            {
                length: Math.max(
                    endPage -
                    startPage +
                    1,
                    0
                )
            },
            (_, index) =>
                startPage + index
        );

    /*
     * =====================================================
     * RESET PAGE
     *
     * 카테고리 / 검색어가 변경되면
     * 무조건 1페이지로 이동
     * =====================================================
     */

    useEffect(() => {
        setCurrentPage(1);
    }, [
        category,
        search
    ]);

    /*
     * =====================================================
     * PAGE GUARD
     *
     * 데이터 삭제 / 검색 결과 변경 등으로
     * 현재 페이지가 존재하지 않게 된 경우 보정
     * =====================================================
     */

    useEffect(() => {
        if (
            totalPages === 0
        ) {
            if (
                currentPage !== 1
            ) {
                setCurrentPage(1);
            }

            return;
        }

        if (
            currentPage >
            totalPages
        ) {
            setCurrentPage(
                totalPages
            );
        }
    }, [
        currentPage,
        totalPages
    ]);

    /*
     * =====================================================
     * PAGE CHANGE
     * =====================================================
     */

    const handlePageChange = (page) => {
        if (
            page < 1 ||
            page > totalPages ||
            page === currentPage
        ) {
            return;
        }

        setCurrentPage(page);

        requestAnimationFrame(() => {
            const portfolioSection =
                document.getElementById(
                    'portfolio-list-section'
                );

            if (!portfolioSection) {
                return;
            }

            const headerHeight = 80;

            const top =
                portfolioSection
                    .getBoundingClientRect()
                    .top +
                window.scrollY -
                headerHeight;

            window.scrollTo({
                top,
                behavior: 'smooth'
            });
        });
    };

    /*
     * =====================================================
     * PREVIOUS
     * =====================================================
     */

    const handlePrev = () => {
        if (
            currentPage <= 1
        ) {
            return;
        }

        handlePageChange(
            currentPage - 1
        );
    };

    /*
     * =====================================================
     * NEXT
     * =====================================================
     */

    const handleNext = () => {
        if (
            currentPage >=
            totalPages
        ) {
            return;
        }

        handlePageChange(
            currentPage + 1
        );
    };

    /*
     * =====================================================
     * RENDER
     * =====================================================
     */

    return (
        <div className="portfolio-page">
            {/* =============================================
                SUB VISUAL
            ============================================= */}

            <section className="portfolio-sub-visual">
                <div className="portfolio-sub-overlay">
                </div>

                <div className="portfolio-sub-inner">
                    <p>
                        PORTFOLIO
                    </p>

                    <h1>
                        시공사례
                    </h1>

                    <div className="portfolio-breadcrumb">
                        <Link to="/">
                            HOME
                        </Link>

                        <span>
                            ›
                        </span>

                        <strong>
                            시공사례
                        </strong>
                    </div>
                </div>
            </section>

            {/* =============================================
                CONTENT
            ============================================= */}

            <section
                id="portfolio-list-section"
                className="portfolio-content"
            >
                <div className="portfolio-container">
                    {/* =====================================
                        TOOLBAR
                    ===================================== */}

                    <div className="portfolio-toolbar">
                        {/* CATEGORY */}

                        <div className="portfolio-categories">
                            {categories.map(
                                (item) => (
                                    <button
                                        type="button"
                                        key={item}
                                        className={
                                            category === item
                                                ? 'active'
                                                : ''
                                        }
                                        onClick={() =>
                                            setCategory(
                                                item
                                            )
                                        }
                                    >
                                        {item}
                                    </button>
                                )
                            )}
                        </div>

                        {/* SEARCH */}

                        <div className="portfolio-search">
                            <input
                                type="text"
                                value={search}
                                placeholder="검색어를 입력하세요."
                                onChange={(e) =>
                                    setSearch(
                                        e.target.value
                                    )
                                }
                            />

                            <i className="mdi mdi-magnify">
                            </i>
                        </div>
                    </div>

                    {/* =====================================
                        RESULT COUNT
                    ===================================== */}

                    {!isLoading && !loadError && (
                        <div className="portfolio-result-info">
                            <span>
                                총
                            </span>

                            <strong>
                                {filteredPortfolio.length}
                            </strong>

                            <span>
                                건
                            </span>
                        </div>
                    )}

                    {/* =====================================
                        LOADING
                    ===================================== */}

                    {isLoading ? (
                        <div className="portfolio-empty">
                            <i className="mdi mdi-loading mdi-spin">
                            </i>

                            <strong>
                                시공사례를 불러오는 중입니다.
                            </strong>

                            <span>
                                잠시만 기다려주세요.
                            </span>
                        </div>
                    ) : loadError ? (
                        /* =================================
                           ERROR
                        ================================= */

                        <div className="portfolio-empty">
                            <i className="mdi mdi-alert-circle-outline">
                            </i>

                            <strong>
                                시공사례를 불러오지 못했습니다.
                            </strong>

                            <span>
                                {loadError}
                            </span>
                        </div>
                    ) : currentPortfolio.length > 0 ? (
                        /* =================================
                           PORTFOLIO GRID
                        ================================= */

                        <div
                            className="portfolio-grid"
                            key={`portfolio-page-${currentPage}`}
                        >
                            {currentPortfolio.map(
                                (item) => (
                                    <Link
                                        to={`/portfolio/${item.id}`}
                                        className="portfolio-card"
                                        key={item.id}
                                    >
                                        {/* IMAGE */}

                                        <div className="portfolio-card-image">
                                            <img
                                                src={item.thumbnail}
                                                alt={item.title}
                                            />
                                        </div>

                                        {/* BODY */}

                                        <div className="portfolio-card-body">
                                            <span className="portfolio-category">
                                                {item.category}
                                            </span>

                                            <h2>
                                                {item.title}
                                            </h2>

                                            <p>
                                                {item.summary}
                                            </p>

                                            <div className="portfolio-card-bottom">
                                                <span>
                                                    {item.date}
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                )
                            )}
                        </div>
                    ) : (
                        /* =================================
                           EMPTY
                        ================================= */

                        <div className="portfolio-empty">
                            <i className="mdi mdi-image-search-outline">
                            </i>

                            <strong>
                                검색 결과가 없습니다.
                            </strong>

                            <span>
                                다른 검색어나 카테고리를
                                선택해주세요.
                            </span>
                        </div>
                    )}

                    {/* =====================================
                        PAGINATION
                    ===================================== */}

                    {!isLoading &&
                        !loadError &&
                        totalPages > 1 && (
                            <div className="portfolio-pagination">
                                {/* PREVIOUS */}

                                <button
                                    type="button"
                                    className="portfolio-page-arrow"
                                    onClick={handlePrev}
                                    disabled={
                                        currentPage === 1
                                    }
                                    aria-label="이전 페이지"
                                >
                                    <i className="mdi mdi-chevron-left">
                                    </i>
                                </button>

                                {/* PAGE NUMBER */}

                                {visiblePages.map(
                                    (page) => (
                                        <button
                                            type="button"
                                            key={page}
                                            className={
                                                currentPage === page
                                                    ? 'portfolio-page-number active'
                                                    : 'portfolio-page-number'
                                            }
                                            onClick={() =>
                                                handlePageChange(
                                                    page
                                                )
                                            }
                                            aria-label={`${page}페이지`}
                                        >
                                            {page}
                                        </button>
                                    )
                                )}

                                {/* NEXT */}

                                <button
                                    type="button"
                                    className="portfolio-page-arrow"
                                    onClick={handleNext}
                                    disabled={
                                        currentPage ===
                                        totalPages
                                    }
                                    aria-label="다음 페이지"
                                >
                                    <i className="mdi mdi-chevron-right">
                                    </i>
                                </button>
                            </div>
                        )}
                </div>
            </section>
        </div>
    );
};

export default PortfolioList;